"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  veilOf,
  watchArrival,
  type VideoSourceKind,
  type VideoVeil,
} from "@/lib/live/video/video-arrival";
import { isIOS } from "@/lib/platform";
import { FrontIcon, RollWidget } from "./roll-widget";
import { useWidgetFront } from "./roll-widget-front";
import type { RollWidgetAnchor } from "./roll-widget-slots";
import { useCornerDrag } from "./use-corner-drag";

const BUBBLE_SIZE_PX = 64;

/**
 * L'orientation de la vignette tant qu'aucune image n'a dit la sienne.
 *
 * Une caméra couchée est le cas ordinaire, et c'est le format qu'avait la
 * vignette quand elle recadrait : la vidéo qui arrive ne déplace donc rien la
 * plupart du temps.
 */
const DEFAULT_ASPECT = 16 / 9;

/**
 * Le plus grand côté d'une vignette de coin : les quatre cinquièmes de la
 * hauteur du rouleau, que le calque donne en `cqh` (`roll-widget-layer.tsx`).
 *
 * Quatre cinquièmes, parce qu'une vignette couchée n'en prend alors que les
 * 9/16, soit 45 % — la part que la tête de lecture accorde déjà au pied du
 * rouleau (`MAX_FOOT_SHARE`). Voir `tileShape`.
 */
const TILE_MAX_SIDE = "80cqh";

/*
  Sous 168 px de large, la vignette n'a plus la place des mots. La pastille la
  plus large (« Direct » et son point, 72 px), les deux boutons (62 px) et
  leurs marges au bord (10 px de chaque côté) font 154 px : en dessous, ils se
  touchent. La pastille se réduit alors à son point, et le voile à son anneau.

  **Une largeur, et non un format.** C'était un format, 0,34, compté sur la
  base de 320 px du bureau. Il ne disait plus rien dès que la vignette se borne
  au rouleau (`TILE_MAX_SIDE`) : la même image debout fait 180 px au
  bureau et 116 sur un téléphone couché, où la pastille passait sous les
  boutons. La vignette est donc un conteneur (`@container`), et c'est sa
  largeur qui décide.

  Le seuil s'écrit en entier dans les classes, `@max-[168px]:` : le scanner de
  Tailwind lit le source, un seuil construit ne serait jamais généré.
*/

/**
 * Le vignettage de l'image : d'où il part, et jusqu'où il fonce.
 *
 * Une image de caméra est un rectangle net posé sur un rouleau sombre, et
 * c'est le bord qui trahit le collage — surtout au pied du rouleau, où la
 * bande borde le clavier sur toute sa largeur. En assombrissant les angles on
 * ne cache rien : les mains sont au centre, le bord ne porte que du mur et du
 * meuble.
 *
 * Il ne commence qu'aux deux tiers pour laisser la zone utile intacte, et il
 * s'arrête à un peu plus du tiers d'opacité — au-delà, l'image paraît regardée
 * par un trou de serrure.
 */
const VIGNETTE_FROM = "62%";
const VIGNETTE_ALPHA = 0.38;
const LIVE_RED = "#ef4444";

type VideoWidgetProps = {
  /**
   * Le flux à montrer. `null` tant qu'il n'y en a pas : la vignette montre
   * alors qui va jouer, à la hauteur exacte qu'aura l'image — c'est elle que le
   * rouleau réserve —, en 16/9 le temps que la source dise son orientation.
   */
  readonly stream?: MediaStream | null;
  /**
   * Un fichier à montrer, au lieu d'un flux : c'est ainsi qu'un replay
   * s'affiche. L'élément n'est alors pas lancé tout seul — c'est la tête de
   * lecture qui le mène (voir `replay-camera.tsx`).
   */
  readonly src?: string | null;
  /** Qui est à l'image. */
  readonly name: string;
  /**
   * Ce qui distingue cette vidéo d'une autre au même nom, pour un lecteur
   * d'écran : « caméra 2 ». À l'écran, l'image suffit.
   */
  readonly label?: string;
  /**
   * Retour de soi : l'image en miroir, comme dans une glace. Sans lui, le
   * pianiste qui lève la main droite se voit lever la gauche.
   */
  readonly mirrored?: boolean;
  /**
   * Le coin où la vignette se pose tant que personne ne l'a glissée ailleurs.
   * Suivi vivant, pas valeur initiale : voir {@link useCornerDrag}.
   */
  readonly anchorByDefault?: RollWidgetAnchor;
  /**
   * Se tenir en bulle tant que personne n'en a décidé autrement. Pour les
   * écrans où une vignette ouverte couvrirait l'essentiel du roll — un
   * téléphone couché : on l'ouvre si on veut la voir.
   *
   * **Suivie tant que la vignette n'a pas été touchée, et plus après.** Ce fut
   * une valeur initiale, capturée au montage ; c'était faux sur le seul
   * appareil visé. Un téléphone arrive toujours par le portrait — le studio y
   * demande de tourner l'appareil avant de se montrer —, donc la valeur retenue
   * au montage décrivait l'orientation qu'on venait de quitter, et la rotation
   * livrait deux vignettes de 320 px sur un rouleau de 260 px de haut. Le
   * premier geste sur la vignette fige son état : faire pivoter l'écran ne
   * referme pas une vidéo qu'on a ouverte.
   */
  readonly collapsedByDefault?: boolean;
  /**
   * Observe l'élément vidéo pendant qu'il montre le flux ; rend de quoi
   * arrêter. Sert à mesurer quand une image reçue s'affiche vraiment.
   */
  readonly observeFrames?: (video: HTMLVideoElement) => () => void;
  /**
   * La pastille « Direct ». Vraie par défaut : une vidéo reçue dans une salle
   * en est une. Un replay n'est pas en direct, et le dire serait faux.
   */
  readonly liveBadge?: boolean;
  /**
   * La hauteur (px) que l'image occupe, à chaque fois qu'elle change, et zéro
   * dès qu'il n'y en a plus — repliée en bulle, démontée, ou simplement plus
   * concernée.
   *
   * C'est de quoi réserver la place sous la tête de lecture
   * (`Studio.setFootReserve`) : la vignette est un élément DOM posé sur un
   * canvas, elle seule connaît sa taille, et cette taille change avec l'écran.
   *
   * **Seulement depuis une ancre du bas.** Ailleurs la vignette n'est plus
   * entre le rouleau et le clavier, donc elle n'occupe la place de personne :
   * la glisser vers le haut rend le pied du rouleau, comme la replier.
   *
   * **À garder stable** (`useCallback`) : c'est une dépendance d'effet, et une
   * flèche écrite dans le rendu rebrancherait l'observateur à chaque image
   * reçue.
   */
  readonly onFootprint?: (height: number) => void;
  /**
   * Des commandes posées sur l'image, **seulement quand la vignette est mise
   * en avant**, et couvrant toute sa surface.
   *
   * Dans un coin, une vignette de 320 px n'a pas la place d'un geste, et on ne
   * cadre pas une caméra sur une image grande comme une carte de visite :
   * l'agrandir fait partie du geste. La vignette ne sait pas ce qu'elles
   * commandent — un récital y met le cadrage de ses caméras.
   *
   * Elle leur passe la **définition de l'image** telle que la source la donne :
   * c'est de là qu'un geste déduit ce qu'il déplace. Zéro tant qu'aucune image
   * n'est arrivée.
   */
  readonly controls?: (frame: { readonly width: number; readonly height: number }) => ReactNode;
  /**
   * La vignette vient de passer au premier plan, ou d'en sortir.
   *
   * Elle seule connaît sa taille, et c'est la taille qui décide de la qualité
   * qu'on demande à la salle : une vignette de coin se contente de la couche du
   * milieu, la même image en grand demande la haute. Voir
   * `VideoEngine.watch`.
   *
   * **À garder stable** (`useCallback`) : c'est une dépendance d'effet.
   */
  readonly onAhead?: (ahead: boolean) => void;
  /**
   * Une transformation CSS posée sur l'image, le temps d'un geste.
   *
   * C'est ainsi qu'un cadrage se dessine à la vitesse du doigt : sur les pixels
   * déjà là, pendant que les vraies données partent amorties vers l'appareil
   * qui filme. Vide le reste du temps.
   */
  readonly previewTransform?: string;
  /**
   * Le format que la vignette prend pendant un aperçu dessiné sur l'image
   * entière de la caméra — celui du cadrage qu'on y dessine, quart de tour
   * compris —, ou `null` pour garder celui de l'image reçue.
   *
   * Seulement au premier plan : c'est là que l'aperçu existe, et une vignette
   * de coin montre l'image reçue telle quelle.
   */
  readonly previewAspect?: number | null;
};

/**
 * Une vidéo posée sur le roll, en vignette : l'image seule, sans en-tête.
 *
 * Pas l'habit des panneaux du parcours (`RollWidgetFrame`) : un panneau se lit,
 * une vignette se regarde, et dans un récital on regarde quelqu'un jouer. Le
 * nom et l'état sont posés sur l'image elle-même.
 *
 * Deux gestes, et pas davantage :
 * - la **glisser** vers un autre coin, où elle se range à la lâcher ;
 * - la **réduire** en bulle, pour rendre tout le roll sans perdre le fait que
 *   quelqu'un est à l'image. Taper la bulle la rouvre.
 *
 * Muette et sans contrôles : le son d'une séance passe par le studio, pas par
 * l'élément vidéo, et deux sorties du même son s'entendraient en écho.
 */
export function VideoWidget({
  stream = null,
  src = null,
  name,
  label,
  mirrored = false,
  anchorByDefault = "bottom-right",
  collapsedByDefault = false,
  observeFrames,
  liveBadge = true,
  onFootprint,
  controls,
  onAhead,
  previewTransform = "",
  previewAspect = null,
}: VideoWidgetProps) {
  const { anchor, boxRef, onDragEnd } = useCornerDrag(anchorByDefault);
  const { front, promote, dismiss } = useWidgetFront();
  // `null` tant que personne n'a touché la vignette : c'est alors l'écran qui
  // décide, et il peut changer d'avis en tournant.
  const [choice, setChoice] = useState<boolean | null>(null);
  // Jamais en bulle au premier plan : une pastille de 64 px au milieu du
  // rouleau ne serait ni une mise en avant ni un coin rendu. Le choix est
  // gardé tel quel — la vignette repliée qu'on met en avant s'ouvre, et se
  // referme en revenant dans son coin.
  const collapsed = front ? false : (choice ?? collapsedByDefault);
  // Le flux qu'on a vu bouger. Une vidéo reçue montre sa première image bien
  // avant les suivantes — celle qui amorce le décodeur, le temps que s'écoule la
  // retenue qui la cale sur les notes — : figée, et en avance sur les notes de
  // toute la retenue. Elle ne passe en direct qu'une fois qu'elle bouge.
  const [moving, setMoving] = useState<VideoPayload>(null);
  // L'élément a rendu les armes sur cette source-là. Retenu par source : en
  // changer est une nouvelle chance, et on ne condamne pas la suivante pour la
  // faute de la précédente.
  const [failed, setFailed] = useState<VideoPayload>(null);
  /**
   * La largeur de l'image sur sa hauteur, telle que la source la donne.
   *
   * Gardée ici et non dans la vignette ouverte : une vidéo repliée en bulle
   * continue de jouer, donc de dire son orientation, et la rouvrir ne doit pas
   * repasser par le 16/9 le temps d'une image.
   */
  const [frame, setFrame] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const natural = frame.width > 0 && frame.height > 0 ? frame.width / frame.height : DEFAULT_ASPECT;
  // Un aperçu dessiné sur l'image entière dit lui-même son format : la
  // vignette le prend, pour que le cadrage qu'on dessine se voie à la forme
  // qu'il aura, et non recadré dans celle de l'image reçue.
  const aspect = front && previewAspect !== null ? previewAspect : natural;
  const reducedMotion = useReducedMotion();

  // Le premier plan se dit à qui montre l'image, et la sortie aussi — y
  // compris quand la vignette se démonte en avant, sans quoi la salle
  // continuerait d'envoyer sa couche haute à un écran qui n'existe plus.
  useEffect(() => {
    if (onAhead === undefined) {
      return;
    }

    onAhead(front);

    return () => onAhead(false);
  }, [front, onAhead]);

  const payload: VideoPayload = stream ?? src;
  // Un flux se reconnaît à son objet, un fichier à son adresse : dans les deux
  // cas, c'est « la source qu'on a vue paraître » qui lève le voile.
  const shown = payload !== null && moving === payload;
  const veil = veilOf({
    kind: payload === null ? null : typeof payload === "string" ? "file" : "stream",
    shown,
    failed: payload !== null && failed === payload,
  });
  // Une image parue **et** vivante. Ce n'est pas la même chose que parue : une
  // piste qui casse en route laisse son élément sur une image morte, et une
  // pastille rouge « Direct » par-dessus serait le mensonge le plus cher de la
  // vignette.
  const live = veil === "none";
  const spoken = label === undefined ? name : `${name}, ${label}`;

  return (
    <RollWidget anchor={anchor} front={front}>
      <motion.div
        ref={boxRef}
        drag
        dragMomentum={false}
        dragSnapToOrigin
        // La tirer la sort du premier plan, vers le coin où on la lâche : un
        // seul geste pour en sortir, et c'est celui qu'on connaît déjà.
        onDragEnd={(event, info) => {
          dismiss();
          onDragEnd(event, info);
        }}
        whileDrag={{ scale: 1.03, cursor: "grabbing" }}
        initial={reducedMotion ? false : { opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
        className="group relative touch-none select-none"
        style={{ cursor: "grab" }}
        aria-label={`Vidéo de ${spoken}`}
        role="region"
      >
        <VideoSurface
          collapsed={collapsed}
          payload={payload}
          name={name}
          spoken={spoken}
          mirrored={mirrored}
          live={live}
          veil={veil}
          liveBadge={liveBadge}
          aspect={aspect}
          frame={frame}
          ahead={front}
          controls={controls}
          observeFrames={observeFrames}
          onMoving={setMoving}
          onFailed={setFailed}
          onFrame={setFrame}
          transform={previewTransform}
          onFront={front ? dismiss : promote}
          onCollapse={() => {
            dismiss();
            setChoice(true);
          }}
          onExpand={() => setChoice(false)}
          // Au centre, la vignette ne borde plus le clavier : elle le couvre.
          // Elle ne réserve donc plus la place sous la tête de lecture, pas
          // plus que depuis une ancre du haut.
          onFootprint={
            !front && (anchor === "bottom-left" || anchor === "bottom-right") ? onFootprint : undefined
          }
        />
      </motion.div>
    </RollWidget>
  );
}

/**
 * La surface de la vignette : l'image, et tout ce qu'on pose dessus.
 *
 * # Une seule boîte pour les deux formes
 *
 * Ouverte ou repliée en bulle, c'est la **même** boîte : seuls sa taille, son
 * arrondi et ce qui l'habille changent.
 *
 * C'étaient deux composants, et ça coûtait l'image. Changer de forme changeait
 * de composant, donc de `<video>` : React démonte l'un et monte l'autre, et le
 * décodeur repart de zéro — un flux réattaché repeint en deux images, un
 * fichier de replay repasse par une requête et une recherche. Entre les deux,
 * la vignette montrait un rectangle noir. Rapporté le 2026-09-21, dans les deux
 * sens : « j'agrandis un widget qui fait déjà tourner la vidéo, la vidéo
 * devient toute noire », « je réduis la caméra hand et la vidéo est noire dans
 * la miniature ».
 *
 * L'élément vidéo est donc **le premier enfant, toujours**, et tout le reste
 * s'écrit en conditions autour de lui — React réconcilie par position, la
 * position ne bouge pas, l'élément survit. Agrandir ou replier ne coûte plus
 * qu'un changement de style.
 *
 * C'est ce qui interdit de remettre une forme dans son composant : le jour où
 * la bulle redeviendrait un `<VideoBubble>`, l'image repartirait de zéro sans
 * que rien ne le dise.
 */
function VideoSurface({
  collapsed,
  payload,
  name,
  spoken,
  mirrored,
  live,
  veil,
  liveBadge,
  aspect,
  frame,
  ahead,
  controls,
  transform,
  observeFrames,
  onMoving,
  onFailed,
  onFrame,
  onFront,
  onCollapse,
  onExpand,
  onFootprint,
}: {
  /** Repliée en bulle : la même boîte, ronde et petite. */
  collapsed: boolean;
  payload: VideoPayload;
  name: string;
  spoken: string;
  mirrored: boolean;
  live: boolean;
  /** Ce qu'on pose par-dessus l'image, et ce qu'on en dit. */
  veil: VideoVeil;
  liveBadge: boolean;
  aspect: number;
  /** La définition de l'image, telle que la source la donne ; zéro avant la première. */
  frame: { readonly width: number; readonly height: number };
  /** La vignette est au premier plan : elle remplit la boîte du centre. */
  ahead: boolean;
  controls?: (frame: { readonly width: number; readonly height: number }) => ReactNode;
  /** La transformation du geste en cours, vide sinon. */
  transform: string;
  observeFrames?: (video: HTMLVideoElement) => () => void;
  onMoving: (payload: VideoPayload) => void;
  onFailed: (payload: VideoPayload) => void;
  onFrame: (frame: { width: number; height: number }) => void;
  onFront: () => void;
  onCollapse: () => void;
  onExpand: () => void;
  onFootprint?: (height: number) => void;
}) {
  // Repliée, la vignette ne borde plus le clavier : elle ne lui prend rien, et
  // le dit en rendant sa réservation (`useFootprint` repose zéro en partant).
  const surfaceRef = useFootprint(collapsed ? undefined : onFootprint);
  const controlsRef = useOwnPointer();
  // Une bulle de 64 px n'a la place d'aucun mot : ce qu'elle a à dire passe par
  // son anneau, et par son titre pour qui survole ou écoute.
  const saying = veil === "loading" ? "Chargement de l’image" : veil === "failed" ? "Image indisponible" : "";

  return (
    <div
      ref={surfaceRef}
      className={`relative overflow-hidden ${
        collapsed
          ? "grid place-items-center rounded-full"
          : "@container [--tile-base:21.5cqw] handheld:[--tile-base:calc(100vw-16px)]"
      }`}
      style={collapsed ? bubbleShape(live && liveBadge) : tileShape(aspect, ahead)}
      title={collapsed ? (saying === "" ? spoken : `${spoken} — ${saying.toLowerCase()}`) : undefined}
      aria-busy={veil === "loading"}
    >
      {/* Le premier enfant, quelle que soit la forme : c'est ce qui fait
          survivre l'élément au repli et à l'agrandissement. */}
      {payload !== null && (
        <SourceVideo
          payload={payload}
          mirrored={mirrored}
          // Le geste de cadrage se dessine sur l'image ouverte ; une bulle de
          // 64 px n'est pas une surface de cadrage.
          transform={collapsed ? "" : transform}
          observeFrames={observeFrames}
          onMoving={onMoving}
          onFailed={onFailed}
          onFrame={onFrame}
        />
      )}

      {/*
        Un voile en dégradé et non un masque CSS : un masque rendrait l'image
        transparente sur ses bords, donc dépendante de ce qu'il y a derrière —
        or derrière, c'est le rouleau, et il n'a pas la même couleur d'un bout
        à l'autre. Le voile, lui, fonce toujours vers le même noir.

        Écrit avant le voile d'attente pour passer dessous : celui-ci floute
        déjà l'image entière, et deux assombrissements l'un sur l'autre
        feraient un angle noir. Une bulle s'en passe : elle est trop petite
        pour avoir des angles.
      */}
      {!collapsed && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(120% 120% at 50% 50%, rgba(0, 0, 0, 0) ${VIGNETTE_FROM}, rgba(0, 0, 0, ${VIGNETTE_ALPHA}) 100%)`,
          }}
        />
      )}

      {veil !== "none" && (
        <div className="absolute inset-0 grid place-items-center" style={veilShape(collapsed, payload !== null)}>
          {collapsed ? (
            <Initials name={name} size={BUBBLE_SIZE_PX} bare />
          ) : (
            /*
              Les initiales disent qui va paraître, le mot dit où on en est.
              Sans lui, l'attente d'un fichier de replay et l'absence de caméra
              se ressemblaient trait pour trait : on regardait un rond de
              lettres sans savoir s'il se passait quelque chose.
            */
            <div className="grid justify-items-center gap-2.5">
              <Initials name={name} size={56} />
              {veil !== "absent" && <VeilWord veil={veil} />}
            </div>
          )}
        </div>
      )}

      {/*
        L'attente d'une bulle, dite par son bord.

        Elle a d'abord battu — un voile qui monte et descend en opacité — et ça
        ne se voyait pas : sur un rond de 64 px, la seule chose qui se lit de
        loin est un mouvement qui tourne. L'anneau se pose **après** le voile
        pour passer dessus, et à l'intérieur du bord plutôt que dessus, où la
        bulle le rognerait.
      */}
      {collapsed && veil === "loading" && (
        <svg
          aria-hidden
          viewBox="0 0 64 64"
          width={BUBBLE_SIZE_PX}
          height={BUBBLE_SIZE_PX}
          className="pointer-events-none absolute inset-0 animate-spin motion-reduce:animate-none"
        >
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="2.5"
            strokeLinecap="round"
            /* Un quart du tour : assez pour se voir, assez peu pour qu'on
               distingue qu'il tourne. Le tour fait 2π·30 ≈ 188,5. */
            strokeDasharray="46 142.5"
          />
        </svg>
      )}

      {/* `z-10` sur la pastille et les boutons : les commandes de cadrage,
          écrites après eux, couvrent toute la vignette de leur surface de
          geste et de l'aperçu qu'elles dessinent. Sans cet ordre explicite,
          le bouton qui renvoie la vignette dans son coin passait sous la
          surface, et rien ne le rendait cliquable. */}
      {!collapsed && liveBadge && (
        <span
          // Toujours là : c'est une requête de conteneur qui retire le mot, et
          // un attribut ne la suit pas.
          title={live ? "Direct" : "Bientôt"}
          className="absolute left-2.5 top-2.5 z-10 flex items-center gap-1.5 rounded-full px-2 py-1 font-mono text-[10px] uppercase leading-none @max-[168px]:p-[5px]"
          style={{
            letterSpacing: "0.14em",
            color: live ? "#ffffff" : "var(--color-text-secondary)",
            background: live ? "rgba(239, 68, 68, 0.9)" : "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <span
            aria-hidden
            className={live ? "animate-pulse" : "hidden @max-[168px]:block"}
            style={{ width: 6, height: 6, borderRadius: "50%", background: live ? "#ffffff" : "currentColor" }}
          />
          {/* Une vignette étroite n'a pas la largeur du mot : le point le dit, et
              le lecteur d'écran l'entend quand même. */}
          <span className="@max-[168px]:sr-only">{live ? "Direct" : "Bientôt"}</span>
        </span>
      )}

      {/* Les deux gestes de la vignette ouverte, posés sur l'image : l'agrandir
          au centre, la réduire en bulle. Effacés tant qu'on ne la survole pas —
          et toujours là où le doigt n'a pas de survol à offrir. */}
      {!collapsed && (
        <div className="absolute right-2.5 top-2.5 z-10 flex items-center gap-1.5">
          <TileButton
            onClick={onFront}
            label={ahead ? "Remettre la vidéo dans le coin" : "Mettre la vidéo en avant"}
          >
            <FrontIcon ahead={ahead} />
          </TileButton>

          <TileButton onClick={onCollapse} label="Réduire la vidéo">
            <svg aria-hidden width="12" height="12" viewBox="0 0 12 12">
              <path d="M2.5 6h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </TileButton>
        </div>
      )}

      {!collapsed && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end px-3 pb-2.5 pt-8"
          style={{
            background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%)",
          }}
        >
          <span className="truncate text-[13px] font-medium" style={{ color: "#ffffff" }}>
            {name}
          </span>
        </div>
      )}

      {/* Les commandes, sur toute la surface : au premier plan, on cadre sur
          l'image elle-même. Le conteneur ne peint **rien** — ni fond ni voile :
          c'est ce qu'il portait encore le 2026-09-20, hérité du temps où il
          n'était qu'une barre au bas de la vignette, et il floutait toute
          l'image. Ce qui se voit appartient aux commandes. */}
      {!collapsed && ahead && controls !== undefined && (
        <div ref={controlsRef} className="absolute inset-0">
          {controls(frame)}
        </div>
      )}

      {/*
        Rouvrir la bulle : un bouton posé dessus, et non la boîte elle-même.

        La boîte porte l'image et doit survivre au changement de forme ; un
        `<button>` ne peut pas devenir un `<div>` sans être refait. Le geste
        s'en va donc dans un calque, qui lui peut naître et mourir avec la
        forme — et un vrai bouton garde ce que le clavier et le lecteur d'écran
        attendent.

        `onTap` de framer et non `onClick` : framer l'annule dès que le geste
        devient un glissement, donc déplacer la bulle ne la rouvre pas.
      */}
      {collapsed && (
        <motion.button
          type="button"
          onTap={onExpand}
          aria-label={`Afficher la vidéo de ${spoken}${saying === "" ? "" : `, ${saying.toLowerCase()}`}`}
          /*
            L'anneau de focus est **intérieur** : la boîte qui porte l'image
            rogne ce qui déborde d'elle (`overflow-hidden`, pour que l'image
            épouse le rond), et un anneau posé dehors y disparaîtrait. Il
            était dehors tant que la bulle **était** cette boîte.
          */
          className="absolute inset-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
          style={{ cursor: "inherit" }}
        />
      )}
    </div>
  );
}

/**
 * La boîte d'une vignette ouverte : le format de la source, dans un carré de
 * 21,5 % de la largeur du rouleau — 100vw moins 16 sur un téléphone —, borné
 * aux quatre cinquièmes de sa hauteur.
 *
 * **Au bureau, une part du rouleau et non des pixels.** C'était 320 px : une
 * vignette taillée pour un portable, qui se perdait dans un coin d'un rouleau
 * de 1 800 px (rapporté le 2026-09-22). Ces 21,5 % redonnent 384 px à ce
 * rouleau-là, et 320 à un rouleau de 1 490. `cqw` se résout sur le calque,
 * conteneur de taille, et non sur la vignette elle-même.
 *
 * La vignette prend l'orientation de son image (`onFrame`) au lieu de la
 * recadrer : une caméra debout donne une vignette debout. C'est le recadrage
 * qui pixelisait les récitals filmés au téléphone — le 2026-09-20, d'une image
 * de 360×640, il n'en gardait qu'une bande de 360×202, jetait les deux tiers
 * du débit avec le reste, puis étirait ce qu'il gardait.
 *
 * **À surface égale, et non à hauteur égale.** Bornée en hauteur comme elle
 * l'était en largeur, une vignette 9/16 tombait à 101 px de large : honnête,
 * et trop petit pour qu'on y lise des doigts. Le carré rend les deux
 * orientations comparables — 320×180 couché, 180×320 debout — et `min()` s'en
 * charge sans requête média ni hook : la largeur est celle de la base, sauf
 * quand le format la ferait dépasser du carré en hauteur.
 *
 * **La base dit la largeur qu'un écran peut offrir, le rouleau la hauteur.**
 * Debout, sur un téléphone, c'est la largeur qui est rare : bord à bord, à
 * huit pixels des bords, la vignette montre enfin ce qu'on est venu voir, là
 * où 208 px sur un écran de 390 n'étaient qu'un moniteur posé dans un coin.
 * Couché, c'est la hauteur, et la base n'en savait rien : 100vw moins 16
 * donnait 752×423 sur un rouleau de 258 px de haut — la vignette couvrait
 * tout, passait sur la barre du studio et montait jusqu'à l'en-tête (rapporté
 * le 2026-09-21). D'où la borne ({@link TILE_MAX_SIDE}) : 206×116 sur ce même
 * téléphone, deux vignettes tiennent côte à côte dans la rangée que le calque
 * y forme, et plus de la moitié du rouleau reste à la musique. Au bureau et
 * debout, le rouleau est assez haut pour qu'elle ne morde pas.
 *
 * La base est en classe et non ici : une requête média ne s'écrit pas dans un
 * attribut `style`, et un hook la ferait clignoter sur le bureau. Le format,
 * lui, rejoint le style par une propriété personnalisée, faute de pouvoir
 * écrire un nombre qui change dans une classe.
 */
function tileShape(aspect: number, ahead: boolean): CSSProperties {
  const side = `min(var(--tile-base), ${TILE_MAX_SIDE})`;

  return {
    ["--tile-aspect" as string]: `${aspect}`,
    /*
      Au premier plan, la vignette remplit la boîte du centre au lieu du carré
      de la base : la plus grande image de ce format qui y tient. `cqw`/`cqh`
      sont les deux côtés de cette boîte — c'est le calque qui en fait un
      conteneur de taille (`roll-widget-layer.tsx`) —, et c'est la seule façon
      d'ajuster à une boîte en gardant un format sans la mesurer en JavaScript.
    */
    width: ahead
      ? "min(100cqw, calc(100cqh * var(--tile-aspect)))"
      : `min(${side}, calc(${side} * var(--tile-aspect)))`,
    maxWidth: "100%",
    aspectRatio: "var(--tile-aspect)",
    borderRadius: "var(--radius-shell)",
    background: "#0b0b10",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "var(--shadow-popin-focus)",
  };
}

/**
 * La boîte d'une bulle : un rond de 64 px.
 *
 * L'anneau rouge dit « en direct » là où la pastille n'a plus la place — et il
 * se tait quand la vignette n'est pas un direct. Sur un replay, où la bulle est
 * l'état par défaut sur téléphone, il annonçait un direct à chaque caméra d'un
 * enregistrement vieux de trois jours : la même contre-vérité que la pastille
 * « Direct », que `liveBadge` retire déjà de la vignette ouverte.
 */
function bubbleShape(onAir: boolean): CSSProperties {
  return {
    width: BUBBLE_SIZE_PX,
    height: BUBBLE_SIZE_PX,
    background: "#0b0b10",
    boxShadow: `0 0 0 2px ${onAir ? LIVE_RED : "rgba(255, 255, 255, 0.12)"}, var(--shadow-popin-focus)`,
  };
}

/**
 * Le voile d'attente, selon la forme et selon qu'il y a une source.
 *
 * Un voile et non un cache : la vidéo doit rester à l'écran pour que le
 * navigateur y présente les images qu'on attend. Sans source, il ne fonce ni
 * ne floute — il n'y a rien derrière à masquer, seulement quelqu'un à nommer.
 */
function veilShape(collapsed: boolean, sourced: boolean): CSSProperties {
  if (collapsed) {
    return sourced
      ? { background: "rgba(11, 11, 16, 0.78)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }
      : {};
  }

  return {
    background: `radial-gradient(120% 90% at 50% 35%, rgba(139, 92, 246, 0.16) 0%, rgba(11, 11, 16, 0) 70%)${
      sourced ? ", rgba(11, 11, 16, 0.78)" : ""
    }`,
    backdropFilter: sourced ? "blur(20px)" : undefined,
    WebkitBackdropFilter: sourced ? "blur(20px)" : undefined,
  };
}

/**
 * Un bouton posé sur l'image de la vignette.
 *
 * `onPointerDown` arrêté : sans lui, le geste partirait glisser la vignette et
 * le clic n'arriverait jamais.
 */
function TileButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={label}
      title={label}
      className="grid h-7 w-7 place-items-center rounded-full opacity-0 transition-opacity focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
      style={{
        color: "#ffffff",
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        cursor: "pointer",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Ce qu'un fichier de replay demande à précharger : « metadata » sur iOS,
 * « auto » ailleurs.
 *
 * Sous iOS, WebKit peut s'en tenir aux métadonnées quoi que dise l'attribut —
 * c'est le réglage des navigateurs qui l'embarquent, Chrome le premier, pas
 * celui de Safari. Le fichier ne s'ouvre alors plus que sur une recherche, et
 * une recherche ne le rouvre que si l'attribut, lui, est sous « auto ». Avec
 * « auto », la recherche d'amorce du replay (`replay-camera.tsx`) partait vers
 * un lecteur qui n'existait pas encore et ne se terminait jamais : vignettes
 * en chargement sans fin sous Chrome iPhone, mesuré le 2026-09-28 sur
 * l'appareil (`loadedmetadata`, `suspend`, puis `seeking` pour toujours).
 * Passer l'attribut à « metadata » a tout débloqué, sans geste.
 *
 * Tout iOS et non Chrome seul : sous Safari, « metadata » suit le même chemin
 * — la recherche ouvre le fichier —, et c'était déjà le seul qui marchait sur
 * réseau mobile. Ailleurs, rien ne bride l'attribut et « auto » garde son
 * avance : le fichier se charge avant qu'on le lance.
 */
function filePreload(): "metadata" | "auto" {
  return isIOS() ? "metadata" : "auto";
}

/**
 * L'élément qui montre la source : un flux de salle, ou un fichier de replay.
 *
 * Un flux se pose dans `srcObject` et se lance tout seul ; un fichier se pose
 * dans `src` et attend qu'on le mène — la tête de lecture du replay décide de
 * son instant et de sa marche. Les deux ne se cumulent pas : `srcObject`
 * l'emporte sur `src`, et laisser l'autre poserait une image qui ne changerait
 * jamais.
 */
function SourceVideo({
  payload,
  mirrored,
  transform = "",
  observeFrames,
  onMoving,
  onFailed,
  onFrame,
}: {
  payload: NonNullable<VideoPayload>;
  mirrored: boolean;
  /** La transformation du geste en cours ; vide le reste du temps. */
  transform?: string;
  observeFrames?: (video: HTMLVideoElement) => () => void;
  onMoving: (payload: VideoPayload) => void;
  onFailed: (payload: VideoPayload) => void;
  onFrame: (frame: { width: number; height: number }) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const file = typeof payload === "string";
  const kind: VideoSourceKind = file ? "file" : "stream";

  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    if (typeof payload === "string") {
      video.srcObject = null;
      video.src = payload;

      return;
    }

    video.removeAttribute("src");
    video.srcObject = payload;
  }, [payload]);

  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    const stop = watchArrival(video, kind, () => onMoving(payload));

    /*
      Cet élément-ci s'en va : plus personne ne montre rien.

      Replier ou agrandir une vignette change sa forme, donc son composant,
      donc son `<video>` : React en refait un, et le décodeur repart de zéro.
      Le fait « on a vu la source paraître » appartenait pourtant à l'élément
      d'avant. Gardé, il laissait la vignette découverte sur un élément vide —
      un rectangle noir le temps que le nouveau charge, sans un mot pour le
      dire. Rendu, le voile revient dire qu'on recharge, et se relève à la
      première image du nouvel élément.
    */
    return () => {
      stop();
      onMoving(null);
    };
  }, [payload, kind, onMoving]);

  /*
    L'aveu d'échec de l'élément, relu sur l'élément.

    Sans lui, un fichier qu'on ne peut pas lire — effacé du dépôt, refusé par
    la porte, abîmé — se confondait avec un fichier qui charge encore : le
    voile annonçait un chargement qui n'aurait jamais de fin.

    Mais un `error` de média n'est pas un verdict définitif : ouvrir une
    nouvelle source remet `video.error` à zéro, et l'élément recharge. On ne
    retient donc pas l'événement, on relit **l'élément** à chaque fois qu'il
    peut avoir changé d'avis — `loadstart` et `emptied` sont les deux moments
    où le média se vide de son erreur. Et l'échec part avec l'élément : un
    `<video>` neuf est une nouvelle chance, pas l'héritier de la faute du
    précédent.
  */
  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    const tell = () => onFailed(video.error === null ? null : payload);

    video.addEventListener("error", tell);
    video.addEventListener("loadstart", tell);
    video.addEventListener("emptied", tell);

    return () => {
      video.removeEventListener("error", tell);
      video.removeEventListener("loadstart", tell);
      video.removeEventListener("emptied", tell);
      onFailed(null);
    };
  }, [payload, onFailed]);

  useEffect(() => {
    const video = videoRef.current;

    if (video === null || observeFrames === undefined) {
      return;
    }

    return observeFrames(video);
  }, [observeFrames]);

  /*
    L'orientation de l'image, dite par l'élément lui-même.

    `resize` et non `loadedmetadata` seul : une caméra de téléphone change de
    format en cours de route — on tourne l'appareil, ou WebKit rouvre la
    capture — et la vignette doit suivre plutôt que recadrer. L'événement est
    celui de l'élément vidéo, pas celui de la fenêtre.

    Lu aussi tout de suite : les métadonnées peuvent être déjà là quand l'effet
    s'exécute, et l'événement ne repassera pas.
  */
  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    const read = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onFrame({ width: video.videoWidth, height: video.videoHeight });
      }
    };

    read();
    video.addEventListener("loadedmetadata", read);
    video.addEventListener("resize", read);

    return () => {
      video.removeEventListener("loadedmetadata", read);
      video.removeEventListener("resize", read);
    };
  }, [payload, onFrame]);

  return (
    <>
      <video
        ref={videoRef}
        autoPlay={!file}
        muted
        playsInline
        preload={file ? filePreload() : undefined}
        /*
          Le flou du geste, décidé par un attribut et non par un état React.

          C'est le calage des images qui sait si la tête de lecture bouge
          (`replay-camera.tsx`), et il tourne à chaque image rendue du rouleau
          — soixante fois par seconde. Lui faire traverser React ferait autant
          de rendus pour une classe CSS. Il pose donc `data-scrubbing`, et le
          style suit tout seul, transition comprise.

          `blur-0` et non l'absence de filtre : c'est entre deux flous qu'une
          transition existe, et on veut la voir monter et redescendre.
        */
        data-scrubbing="false"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover blur-0 transition-[filter] duration-200 ease-out data-[scrubbing=true]:blur-[7px]"
        /*
          Le miroir écrit en premier, donc appliqué en dernier : c'est une glace
          posée devant le résultat. Le geste, lui, décrit ce que l'image
          publiée fait dans son propre repère, et vue dans une glace, une image
          qui glisse à droite glisse à gauche. Écrit dans l'autre ordre, le
          geste partait à contresens du doigt sur une image en miroir.
        */
        style={{ transform: `${mirrored ? "scaleX(-1)" : ""} ${transform}`.trim() || undefined }}
      />

    </>
  );
}

/**
 * Garde les gestes qui commencent ici pour l'élément qui les reçoit : sans
 * cela, la vignette part se déplacer dès qu'on touche un curseur.
 *
 * Un écouteur **natif** et non un `onPointerDown` de React. React délègue ses
 * écouteurs à la racine du document : son gestionnaire s'exécute donc *après*
 * celui que framer-motion pose sur la vignette elle-même, qui a déjà décidé
 * qu'un glissement commençait et capturé le pointeur. Le curseur ne recevait
 * plus ni mouvement ni relâchement — il ne bougeait pas, et ne publiait rien.
 * Posé sur la barre, l'écouteur natif arrête l'événement avant qu'il ne remonte
 * jusqu'à elle, sans priver l'`input` de ce qui lui revient.
 */
function useOwnPointer() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const bar = ref.current;

    if (bar === null) {
      return;
    }

    const keep = (event: PointerEvent) => event.stopPropagation();

    bar.addEventListener("pointerdown", keep);

    return () => bar.removeEventListener("pointerdown", keep);
  }, []);

  return ref;
}

/** Ce qu'une vignette montre : un flux de salle, un fichier, ou rien encore. */
type VideoPayload = MediaStream | string | null;

/**
 * Ce que le voile dit sous les initiales : qu'on attend, ou qu'on n'attend
 * plus.
 *
 * Étroite, la vignette n'a plus la largeur des mots — elle en a déjà retiré la
 * pastille, au même seuil (`@max-[168px]`, voir en tête de fichier) : l'anneau
 * tourne seul, et le titre dit le reste à qui survole. Un lecteur d'écran, lui,
 * l'entend dans les deux cas.
 */
function VeilWord({ veil }: { veil: VideoVeil }) {
  const word = veil === "failed" ? "Image indisponible" : "Chargement";

  return (
    <span
      title={word}
      className="flex items-center gap-1.5 font-mono text-[10px] uppercase leading-none"
      style={{ letterSpacing: "0.14em", color: "var(--color-text-secondary)" }}
    >
      {veil === "loading" && <Spinner />}
      <span className="@max-[168px]:sr-only">{word}</span>
    </span>
  );
}

/** L'anneau qui tourne : il ne mesure rien, il dit que ça travaille. */
function Spinner() {
  return (
    <svg
      aria-hidden
      width="11"
      height="11"
      viewBox="0 0 12 12"
      className="animate-spin motion-reduce:animate-none"
    >
      <circle cx="6" cy="6" r="4.6" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
      <path
        d="M6 1.4A4.6 4.6 0 0 1 10.6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Initials({ name, size, bare = false }: { name: string; size: number; bare?: boolean }) {
  const letters = name
    .split(/\s+/)
    .filter((part) => part !== "")
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <span
      aria-hidden
      className="grid place-items-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.34),
        color: "var(--color-text-primary)",
        background: bare ? "transparent" : "rgba(255, 255, 255, 0.06)",
        border: bare ? "none" : "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      {letters}
    </span>
  );
}

/**
 * Mesure la vignette et dit la hauteur à réserver sous la tête de lecture.
 *
 * Jamais plus que sa largeur : une vignette en portrait est aussi haute que
 * large en paysage, et réserver toute sa hauteur perchait la tête de lecture
 * au milieu du rouleau. Plafonnée ainsi, elle réserve ce qu'une caméra en
 * paysage de même taille réserverait, et son haut déborde sur les notes.
 *
 * L'observateur est branché **ici et non dans `VideoWidget`** : le widget
 * traverse un portail, et tant que l'ancre du calque n'est pas montée
 * `RollWidget` ne rend rien. Un effet écrit chez le parent s'exécuterait donc
 * sur un DOM vide, une seule fois, et ne verrait jamais la vignette arriver.
 * Ici, le composant lui-même n'existe qu'une fois posé.
 *
 * `offsetHeight` et non le rectangle du client : la vignette grossit de 3 %
 * sous le doigt qui la glisse, et la place réservée sous la tête de lecture ne
 * doit pas suivre ce geste-là.
 *
 * Le nettoyage rend la place. C'est ce qui fait qu'une vignette repliée en
 * bulle, ou qui cesse d'être concernée en tournant l'écran, ne laisse pas la
 * tête de lecture perchée au-dessus de rien.
 */
function useFootprint(report: ((height: number) => void) | undefined) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tile = ref.current;

    if (tile === null || report === undefined) {
      return;
    }

    const measure = () => report(Math.min(tile.offsetHeight, tile.offsetWidth));

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(tile);

    return () => {
      observer.disconnect();
      report(0);
    };
  }, [report]);

  return ref;
}
